import React, { useState, useRef } from 'react';


const ScannerForm = ({ onStartScan, onImageUpload, onBulkUpload }) => {
  const [image, setImage] = useState(null);
  const [description, setDescription] = useState('');
  const [error, setError] = useState('');
  const [step, setStep] = useState('capture'); // 'capture' or 'describe'
  const [bulkFiles, setBulkFiles] = useState([]); // Add this missing state
  const fileInputRef = useRef(null);
  const bulkInputRef = useRef(null);

  // Handle camera button click - no description needed first
  const handleCameraClick = (e) => {
    e.preventDefault();
    onStartScan(); // No description parameter
  };

  // Handle single file upload
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (event) => {
      setImage(event.target.result);
      setStep('describe');
      e.target.value = '';
    };
    reader.readAsDataURL(file);
  };

  // Handle bulk file upload
  const handleBulkUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    if (files.length > 20) {
      setError('You can upload a maximum of 20 images at once');
      e.target.value = '';
      return;
    }
    
    // Move to description step with files
    onBulkUpload(files);
    e.target.value = '';
  };

  // Submit description after image is captured/uploaded
  const handleDescriptionSubmit = (e) => {
    e.preventDefault();
    
    if (!description.trim()) {
      setError('Please enter a description of what you want to scan for');
      return;
    }
    
    if (image) {
      // For single image
      onImageUpload(description, image);
      // Reset state
      setImage(null);
      setDescription('');
      setStep('capture');
    } else if (bulkFiles && bulkFiles.length > 0) {
      // For bulk images
      onBulkUpload(description, bulkFiles);
      // Reset state
      setBulkFiles([]);
      setDescription('');
      setStep('capture');
    }
  };

  // Cancel button for returning to capture step
  const handleCancel = () => {
    setImage(null);
    setBulkFiles([]);
    setDescription('');
    setStep('capture');
  };

  return (
    <div className="mt-6 max-w-4xl mx-auto sm:px-0">
      <h2 className="text-3xl font-bold mb-6">Product ID Scanner</h2>
      <div className="bg-blue-50 rounded-lg shadow-sm p-6 mb-6">
          
          <div className="text-sm font-semibold text-blue-600 space-y-3">
            
            <h3 className="text-xl text-blue-600 font-semibold mb-4">How It Works:</h3>
            <p>
              This scanner helps you quickly extract and organize product identification numbers 
              from labels, packaging, or documentation using your device's camera.
            </p>
            <ol className="list-decimal text-sm font-semibold text-blue-600 ml-5 space-y-2">
              <li>Capture an image using your camera or upload from your gallery</li>
              <li>Specify what type of ID you're looking for (Serial Number, PO Number, etc.)</li>
              <li>The system will automatically identify and extract the requested information</li>
              <li>View your results immediately and access them later from your history</li>
            </ol>
            <p>
              Then export the CSV and easily create QR codes
            </p>
            <h3 className="text-lg text-blue-600 font-semibold mb-3">Tips for Best Results:</h3>
            <ul className="list-disc text-sm font-semibold text-blue-600 ml-5 space-y-2">
              <li>Ensure good lighting for clear text visibility</li>
              <li>Make sure text is clearly visible and not blurry</li>
            </ul>
          </div>
        </div>
      <div className="bg-white px-6 rounded-lg shadow-sm p-6 mb-6">
        
        {step === 'capture' ? (
          // Step 1: Capture/upload image first
          <form>
                    <h2 className="block mt-2 text-m text-gray-700 font-semibold mb-1">Take or Upload an Images</h2>
        <p className="text-sm text-gray-400 mb-4">
        Taking photos directly or uploading existing images.
        </p>
            <div className="flex flex-col sm:flex-row gap-3">
              
              <button 
                type="button"
                onClick={handleCameraClick}
                className="flex-1 bg-blue-700 hover:bg-blue-800 text-white font-semibold py-3 px-4 rounded-md transition-colors duration-200"
              >
                Use Camera
              </button>
              
              <input 
                type="file"
                ref={fileInputRef}
                accept="image/*"
                onChange={handleFileChange}
                className="hidden"
              />
              
              {/* <button 
                type="button"
                onClick={() => fileInputRef.current.click()}
                className="flex-1 bg-green-500 hover:bg-green-600 text-white font-medium py-3 px-4 rounded-md transition-colors duration-200"
              >
                Upload Image
              </button> */}
              
              <input 
                type="file"
                ref={bulkInputRef}
                accept="image/*"
                multiple
                onChange={handleBulkUpload}
                className="hidden"
              />
              
              <button 
                type="button"
                onClick={() => bulkInputRef.current.click()}
                className="flex-1 bg-purple-700 hover:bg-purple-800 text-white font-semibold py-3 px-4 rounded-md transition-colors duration-200"
              >
                Upload Image(s)
              </button>
              
            </div>
            <div className="mt-3 mb-4 flex justify-center">
  <p className="text-sm text-gray-400">
    Upload up to 20 images at a time.
  </p>
</div>
          </form>
        ) : (
          // Step 2: Enter description after image is captured/uploaded
          <form onSubmit={handleDescriptionSubmit}>
            {image && (
              <div className="mb-4">
                <img 
                  src={image} 
                  alt="Captured" 
                  className="w-full max-h-64 object-contain rounded-lg" 
                />
              </div>
            )}
            
            <div className="mb-4">
              <label 
                htmlFor="description" 
                className="block mt-2 text-m text-gray-700 font-semibold mb-1"
              >
                What would you like to extract from this image?
              </label>
              <p className="text-sm text-gray-400 pb-3">
                Enter a keyword (like 'serial', 'ID', 'IMEI') or specific text pattern you're looking for.
              </p>
              <input
                type="text"
                id="description"
                value={description}
                onChange={(e) => {
                  setDescription(e.target.value);
                  if (e.target.value.trim()) setError('');
                }}
                placeholder="E.g., Product ID, Serial Number, Barcode, Phone Number, Country"
                className={`w-full px-4 py-3 border ${error ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition`}
              />
              {error && (
                <p className="mt-2 text-sm text-red-600">{error}</p>
              )}
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3">
              <button 
                type="button"
                onClick={handleCancel}
                className="flex-1 bg-gray-500 hover:bg-gray-600 text-white font-semibold py-3 px-4 rounded-md transition-colors duration-200"
              >
                Cancel
              </button>
              
              <button 
                type="submit" 
                className="flex-1 bg-blue-700 hover:bg-blue-800 text-white font-semibold py-3 px-4 rounded-md transition-colors duration-200"
              >
                Process Image
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default ScannerForm;